import { Link } from 'react-router';
import { useRuns } from '../api/runs';
import { isTerminalRunState } from '../lib/runState';
import { InlineError } from './InlineError';
import { Skeleton } from './Skeleton';
import { StateBadge } from './StateBadge';
import { RelativeTime } from './RelativeTime';
import { RunArtifactLinks } from './RunArtifactLinks';
import { CancelRunButton } from './CancelRunButton';

export function RunsList({ jobId }: { jobId: string }) {
  const { data, error, isPending, refetch } = useRuns(jobId);

  if (isPending) {
    return <Skeleton variant="rows" rows={2} rowClassName="h-12" className="space-y-2" />;
  }
  if (error) {
    return (
      <InlineError heading="Couldn't load runs" error={error} onRetry={() => void refetch()} />
    );
  }
  if (!data || data.length === 0) {
    return <p className="text-sm text-slate-500">No runs yet.</p>;
  }

  // Newest-first by the timestamp the row displays (updatedAt). `slice()` first
  // so we never mutate the TanStack Query cache array in place.
  const runs = data
    .slice()
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

  return (
    <ul className="space-y-3">
      {runs.map((r) => {
        const terminal = isTerminalRunState(r.state);
        return (
          <li key={r.id} className="rounded border border-slate-200 bg-white p-3">
            {/* Link wraps only the run-summary row; artifact links below stay
              independently clickable so opening an artifact doesn't navigate
              to the run-detail page. */}
            <Link
              to={`/jobs/${jobId}/runs/${r.id}`}
              className="flex items-center gap-2 text-sm hover:bg-slate-50"
            >
              <StateBadge state={r.state} />
              <span className="font-mono text-xs text-slate-500">{r.id.slice(0, 8)}</span>
              {r.retryCount > 0 && (
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-xs text-amber-800">
                  retry {r.retryCount}
                </span>
              )}
              <span className="ml-auto text-xs text-slate-400">
                <RelativeTime iso={r.updatedAt} />
              </span>
            </Link>
            {/* Cancel lives OUTSIDE the Link so clicking it tears down the run
              instead of navigating. CancelRunButton self-hides on terminal runs;
              the guard mirrors the existing terminal-flag style and avoids an
              empty flex row. */}
            {!terminal && (
              <div className="mt-2 flex justify-end">
                <CancelRunButton jobId={jobId} runId={r.id} runState={r.state} size="sm" />
              </div>
            )}
            {/* RunArtifactLinks renders null for non-terminal/empty/error runs, so only
              wrap it in the separator when there's a chance of content (terminal run);
              an in-flight run shows no dangling bordered box. */}
            {terminal && (
              <div className="mt-2 border-t border-slate-100 pt-2 empty:hidden">
                <RunArtifactLinks jobId={jobId} runId={r.id} terminal={terminal} />
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
